import NumberTextField from '@/components/common/NumberTextField';
import TextFieldWrapper from '@/components/common/TextFieldWrapper';
import { ConfigurationString } from '@/types/metadata/metadata';
import { isEmptyValue, isNumber, isString } from '@/util/typeGuards';
import { textblockUtil } from '@/util/validation/textblock';
import { Abc, PinOutlined } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { Checkbox, Fade, InputAdornment, Tooltip } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { ParamProps } from './ParamRenderer';

const EndAdornment: FC<{
  type: string | undefined;
  abcProps: {
    showNumChoice?: boolean;
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
  };
  nullableProps?: {
    checked?: boolean;
    onChange: (checked: boolean) => void;
  };
}> = ({ type, abcProps, nullableProps }) => {
  const abcTooltipTitle = `Store as ${abcProps.checked ? 'integer' : 'string'}`;
  const nullableTooltipTitle = `${nullableProps?.checked ? 'String is empty' : 'Empty string'}`;
  return (
    <InputAdornment position='end'>
      {nullableProps && (
        <Tooltip title={nullableTooltipTitle} placement='top' arrow>
          <Checkbox
            icon={<CancelIcon />}
            checkedIcon={<CancelPresentationIcon />}
            checked={nullableProps.checked || false}
            onChange={(e, checked) => nullableProps.onChange(checked)}
            color='default'
          />
        </Tooltip>
      )}
      <Fade in={abcProps.showNumChoice} unmountOnExit>
        <Tooltip title={abcTooltipTitle} placement='top' arrow>
          <Checkbox
            disabled={abcProps.disabled}
            icon={<Abc />}
            checkedIcon={<PinOutlined />}
            checked={abcProps.checked}
            onChange={(e, checked) => abcProps.onChange && abcProps.onChange(checked)}
          />
        </Tooltip>
      </Fade>
      {type && <code>{type}</code>}
    </InputAdornment>
  );
};

const ParamTextField: FC<ParamProps> = (props) => {
  const configuration = props.configuration as ConfigurationString;

  const initialValue = textblockUtil().getValue(props.configuration, props.initialValue);

  const [returnAsNumber, setReturnAsNumber] = useState(configuration.subtype === 'integer' || isNumber(initialValue));
  const [emptyIsChecked, setEmptyIsChecked] = useState(isEmptyValue(initialValue));
  const showNumChoice =
    !configuration.subtype || configuration.subtype === 'any' || configuration.subtype === 'str/int';

  const handleOnAbcCheckedChange = (checked: boolean) => {
    setReturnAsNumber(checked);

    if (isEmptyValue(initialValue)) {
      setEmptyIsChecked(false);
      props.onChange([configuration.value, '']);
    } else {
      props.onChange([configuration.value, convertValue(checked, initialValue)]);
    }
  };

  const handleOnNullableCheckedChange = (checked: boolean) => {
    checked ? props.onChange([configuration.value, { empty: true }]) : props.onChange([configuration.value, '']);
    setEmptyIsChecked(checked);
  };

  const handleTextChange = (newValue: string) => {
    if (!newValue && emptyIsChecked) return;
    props.onChange([configuration.value, newValue]);
    setEmptyIsChecked(false);
  };

  const convertValue = (shouldBeNumber: boolean, value: unknown) => {
    if (shouldBeNumber) {
      return isString(value) ? parseInt(value) : value;
    }

    return isNumber(value) ? value.toString() : value;
  };

  useEffect(() => {
    setReturnAsNumber(
      configuration.subtype === 'integer' ||
        configuration.subtype === 'decimal' ||
        configuration.subtype === 'numeric' ||
        isNumber(initialValue)
    );
  }, [configuration.subtype]);

  useEffect(() => setEmptyIsChecked(isEmptyValue(initialValue)), [initialValue]);

  return returnAsNumber ? (
    <NumberTextField
      errors={props.errors}
      required={props.configuration.required}
      numberType={configuration.subtype}
      size='small'
      fullWidth
      label={configuration.label}
      value={emptyIsChecked ? '' : initialValue}
      onChange={(value) => props.onChange([configuration.value, value])}
      InputProps={{
        endAdornment: (
          <EndAdornment
            type={configuration.subtype}
            abcProps={{
              showNumChoice: showNumChoice,
              checked: returnAsNumber,
              onChange: (checked) => handleOnAbcCheckedChange(checked)
            }}
          />
        )
      }}
    />
  ) : (
    <TextFieldWrapper
      errors={props.errors}
      required={configuration.required && !emptyIsChecked}
      size='small'
      fullWidth
      label={configuration.label}
      value={initialValue}
      onChange={(event) => handleTextChange(event.currentTarget.value)}
      multiline={configuration.type === 'textblock'}
      minRows={configuration.type === 'textblock' ? 4 : 0}
      InputProps={{
        endAdornment: (
          <EndAdornment
            type={configuration.subtype}
            abcProps={{
              showNumChoice: showNumChoice,
              checked: returnAsNumber,
              onChange: (checked) => handleOnAbcCheckedChange(checked)
            }}
            nullableProps={{
              checked: emptyIsChecked,
              onChange: (checked) => handleOnNullableCheckedChange(checked)
            }}
          />
        )
      }}
    />
  );
};

export default ParamTextField;
